/**
 * Company: SPHMedia
 * Description: This is the base page component for all BH pages
 */

import CASBlock from "@components/Blocks/CASBlock/CASBlock";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import { MainContainerNew } from "@components/Blocks/Container/MainContainerNew";
import { FirebaseNotificationPrompt } from "@components/Blocks/FirebaseNotificationPrompt/FirebaseNotificationPrompt";
import CatfishAd from "@components/Elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@components/Elements/Advertisement/helpers";
import PrestitialAd from "@components/Elements/Advertisement/PrestitialAd";
import { FooterNew } from "@components/Modules/FooterNew/FooterNew";
import FooterNewV2 from "@components/Modules/FooterNew/FooterNewV2";
import { HeaderNew } from "@components/Modules/HeaderNew/HeaderNew";
import { HeaderNewV2 } from "@components/Modules/HeaderNew/HeaderNewV2";
import { QnFloatButton } from "@components/Modules/QuickNews/QnButtons";
import { pubPageAdTargeting } from "@config/ads";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import {
  CAS_EI_MODEL_ID,
  ENABLE_NEW_HEADER_FOOTER,
  IS_PLAYWRIGHT_TEST,
  QUICKNEWS_FLAG,
} from "@helper/getEnvVariables";
import { useNeuronPageViewPush } from "@hooks/useAnalyticsCall/useNeuronPageViewPush";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useHideInvisibleAds } from "@hooks/useHideInvisibleAds";
import useSendPermutiveIdentify from "@hooks/useSendPermutiveIdentify";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetValue,
} from "@typings/Ads.d";
import { ReactElement } from "react";
import { useLocation } from "react-router";
import { twMerge } from "tailwind-merge";

export type BHBasePageProps = {
  uniqueSectionName?: string;
  adsSectionName?: string;
  children?: React.ReactNode;
  hasLb1Ads?: boolean;
  pageAdTargetValue: PageAdTargetValue;
  hideScrollableSectionMenu?: boolean;
  adsSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  bodyWrapperStyle?: string;
  showNDRBanner?: boolean;
};

/**
 * BHBasePage
 * Description: The base page component for all BH pages
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function BHBasePage({
  uniqueSectionName,
  adsSectionName = uniqueSectionName,
  children,
  hasLb1Ads = true,
  pageAdTargetValue,
  hideScrollableSectionMenu = false,
  adsSlotTarget,
  bodyWrapperStyle = "",
  showNDRBanner = true,
}: BHBasePageProps): ReactElement {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isMobWebView = searchParams.get("mobwebview");
  const {
    isPrestitialEnabled,
    isCatfishEnabled,
    topOverlayImpressions,
    topOverlayValidity,
  } = useGlobalAdSetting();
  useNeuronPageViewPush();
  useSendPermutiveIdentify();
  useHideInvisibleAds();

  const pageAdTarget = pubPageAdTargeting[pageAdTargetValue] as AdTargetingType<
    string,
    PageAdTargetValue
  >;
  // Sets quick news reference code.
  const qnRefcode =
    uniqueSectionName === "home" ? "home-quick-news" : "listing-quick-news";

  const renderPage = () => {
    return (
      <>
        <MainContainerNew>
          <AdsContainer className="flex justify-center border-b-[1px] border-black-900">
            {hasLb1Ads && !isMobWebView && (
              <AdsLb1
                uniqueSectionName={adsSectionName}
                pageAdTargetType={pageAdTargetValue}
                hasStripeBg={false}
                wrapperStyle="mb-0"
                articleSlotTarget={adsSlotTarget}
              />
            )}
          </AdsContainer>
          {!isMobWebView &&
            (ENABLE_NEW_HEADER_FOOTER ? (
              <HeaderNewV2 showNDRBanner={showNDRBanner} />
            ) : (
              <HeaderNew
                isHomePage={uniqueSectionName === "home" ? true : false}
                hideScrollableSectionMenu={hideScrollableSectionMenu}
                showNDRBanner={showNDRBanner}
              />
            ))}
          {IS_PLAYWRIGHT_TEST !== "true" && QUICKNEWS_FLAG && (
            <QnFloatButton
              refCode={qnRefcode}
              isCatfishEnabled={isCatfishEnabled}
            />
          )}
          <div className={twMerge("my-3 lg:my-md w-full", bodyWrapperStyle)}>
            {IS_PLAYWRIGHT_TEST !== "true" && (
              <>
                <PrestitialAd
                  slot={GoogleAdsSlotFactory.prestitial(adsSectionName)}
                  isPrestitialEnabled={isPrestitialEnabled}
                  pubAdTargeting={[pageAdTarget]}
                  topOverlayImpressions={topOverlayImpressions}
                  topOverlayValidity={topOverlayValidity}
                  articleSlotTarget={adsSlotTarget}
                />
                <FirebaseNotificationPrompt />
              </>
            )}
            {children}
          </div>
        </MainContainerNew>
        {!isMobWebView &&
          (ENABLE_NEW_HEADER_FOOTER ? <FooterNewV2 /> : <FooterNew />)}
      </>
    );
  };

  return (
    <>
      {renderPage()}
      {isCatfishEnabled && (
        <CatfishAd
          slot={GoogleAdsSlotFactory.catfish(adsSectionName)}
          isCatfishEnabled={isCatfishEnabled}
          pubAdTargeting={[pageAdTarget]}
          articleSlotTarget={adsSlotTarget}
        />
      )}
      <CASBlock casId={CAS_EI_MODEL_ID} />
    </>
  );
}
